/**
 * TUVI - Evidencias (fotos/documentos) por actividad/semana del cronograma (Fase 2).
 * Guarda en el bucket evidencia-cronograma, organizado por proyecto + apto.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import client from '../../../services/graphqlClient';
import { UPLOAD_URL } from '../../../config';
import './ModalEvidencias.css';

const imgUrl = (ruta) => `${UPLOAD_URL}/catalogo/img/evidencia-cronograma/${ruta}`;

const ModalEvidenciasCronograma = ({ show, onClose, actividad, onCambio }) => {
  const { success, error, warning } = useNotification();
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [formData, setFormData] = useState({ archivo: null, descripcion: '', fecha_captura: new Date().toISOString().split('T')[0] });

  const cargar = useCallback(async () => {
    if (!actividad) return;
    try {
      setLoading(true);
      const r = await client.query(`query($id: Int!) { evidenciasCronograma(cronograma_id: $id) { id tipo nombre_archivo ruta_archivo descripcion fecha_captura } }`, { id: actividad.id });
      setEvidencias(r.evidenciasCronograma || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [actividad]);

  useEffect(() => { if (show && actividad) cargar(); }, [show, actividad, cargar]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData(prev => ({ ...prev, archivo: file }));
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setVistaPrevia(reader.result);
      reader.readAsDataURL(file);
    } else setVistaPrevia(null);
  };

  const subir = async (e) => {
    e.preventDefault();
    if (!formData.archivo) { warning('Selecciona un archivo'); return; }
    setSubiendo(true);
    try {
      const data = new FormData();
      data.append('cronograma_id', actividad.id);
      data.append('archivo', formData.archivo);
      data.append('descripcion', formData.descripcion);
      data.append('fecha_captura', formData.fecha_captura);
      const token = localStorage.getItem('tuvi_token');
      const res = await fetch(`${UPLOAD_URL}/cronograma-evidencia`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: data });
      const result = await res.json();
      if (result.success) {
        success('Evidencia subida');
        setFormData({ archivo: null, descripcion: '', fecha_captura: new Date().toISOString().split('T')[0] });
        setVistaPrevia(null);
        const fi = document.getElementById('crono-evid-file'); if (fi) fi.value = '';
        cargar(); onCambio && onCambio();
      } else error('Error: ' + result.message);
    } catch (err) { error('Error al subir la evidencia'); }
    finally { setSubiendo(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta evidencia?')) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarEvidenciaCronograma(id: $id) { success message } }`, { id });
      if (r.eliminarEvidenciaCronograma.success) { success('Evidencia eliminada'); cargar(); onCambio && onCambio(); }
      else error(r.eliminarEvidenciaCronograma.message);
    } catch (e) { error('Error al eliminar'); }
  };

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-evidencias" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📸 Evidencias — {actividad?.etiqueta_semana || `Semana ${actividad?.orden}`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-subheader"><p>{actividad?.nombre}</p></div>

        <form onSubmit={subir} className="evidencia-upload-form">
          <div className="upload-section">
            <div className="file-input-wrapper">
              <input type="file" id="crono-evid-file" accept="image/*,application/pdf" onChange={handleFile} className="file-input" />
              <label htmlFor="crono-evid-file" className="file-input-label">📁 Seleccionar Foto o Documento</label>
              {formData.archivo && <span className="file-name">{formData.archivo.name}</span>}
            </div>
            {vistaPrevia && <div className="vista-previa"><img src={vistaPrevia} alt="Vista previa" /></div>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Descripción (opcional)</label>
              <input type="text" value={formData.descripcion} onChange={e => setFormData(p => ({ ...p, descripcion: e.target.value }))} placeholder="Describe esta evidencia..." />
            </div>
            <div className="form-group">
              <label>Fecha de Captura</label>
              <input type="date" value={formData.fecha_captura} onChange={e => setFormData(p => ({ ...p, fecha_captura: e.target.value }))} />
            </div>
          </div>
          <button type="submit" className="btn-subir" disabled={subiendo || !formData.archivo}>{subiendo ? 'Subiendo...' : '⬆️ Subir Evidencia'}</button>
        </form>

        <div className="evidencias-lista">
          <h3>📚 Evidencias ({evidencias.length})</h3>
          {loading ? (
            <div className="loading-state"><span>⏳</span><p>Cargando...</p></div>
          ) : evidencias.length === 0 ? (
            <div className="empty-state"><span>📷</span><p>No hay evidencias</p><small>Sube la primera de esta semana</small></div>
          ) : (
            <div className="evidencias-grid">
              {evidencias.map(ev => (
                <div key={ev.id} className="evidencia-card">
                  <div className="evidencia-preview">
                    {ev.tipo === 'foto' ? (
                      <img src={imgUrl(ev.ruta_archivo)} alt={ev.descripcion || 'Evidencia'} onClick={() => window.open(imgUrl(ev.ruta_archivo), '_blank')} />
                    ) : (
                      <div className="documento-preview" onClick={() => window.open(imgUrl(ev.ruta_archivo), '_blank')}>
                        <span className="documento-icon">📄</span><span className="ver-icon">👁️</span>
                      </div>
                    )}
                    <span className="evidencia-tipo-badge">{ev.tipo === 'foto' ? '📷 Foto' : '📄 Documento'}</span>
                  </div>
                  <div className="evidencia-info">
                    {ev.descripcion && <p className="evidencia-descripcion">{ev.descripcion}</p>}
                    {ev.fecha_captura && <span className="evidencia-fecha">📅 {new Date(ev.fecha_captura + 'T12:00:00').toLocaleDateString('es-CO')}</span>}
                  </div>
                  <div className="evidencia-actions">
                    <button className="btn-ver" onClick={() => window.open(imgUrl(ev.ruta_archivo), '_blank')}>👁️ Ver</button>
                    <button className="btn-eliminar-evidencia" onClick={() => eliminar(ev.id)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalEvidenciasCronograma;
